import { ChevronLeft, ChevronRight } from 'lucide-react';
interface PaginationProps {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  jumpToPage: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onJumpToPage: () => void;
  onJumpInputChange: (value: string) => void;
  onJumpInputKeyDown: (e: React.KeyboardEvent) => void;
  isLoading: boolean;
}
export function Pagination({
  currentPage,
  pageSize,
  totalItems,
  jumpToPage,
  onPageChange,
  onPageSizeChange,
  onJumpToPage,
  onJumpInputChange,
  onJumpInputKeyDown,
  isLoading,
}: PaginationProps) {
  if (isLoading) return null;
  return (
    <div className="flex items-center justify-between px-6 py-4 bg-white border-t border-gray-200">
      <div className="flex items-center gap-4">
        <div className="text-sm text-gray-500">
          {totalItems > 0
            ? `Showing ${(currentPage - 1) * pageSize + 1} to ${Math.min(
                currentPage * pageSize,
                totalItems
              )} of ${totalItems} results`
            : 'Showing 0 results'}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Show:</span>
          <select
            value={pageSize}
            onChange={e => onPageSizeChange(Number(e.target.value))}
            className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
          <span className="text-sm text-gray-500">per page</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1 || totalItems === 0}
          className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded transition-colors ${
            currentPage <= 1 || totalItems === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <ChevronLeft size={14} />
          Previous
        </button>
        <div className="flex gap-1">
          {Array.from(
            { length: Math.min(5, Math.ceil(totalItems / pageSize)) },
            (_, i) => {
              const totalPages = Math.ceil(totalItems / pageSize);
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  className={`px-3 py-1.5 text-sm rounded transition-colors ${
                    currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {pageNum}
                </button>
              );
            }
          )}
        </div>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={
            currentPage >= Math.ceil(totalItems / pageSize) || totalItems === 0
          }
          className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded transition-colors ${
            currentPage >= Math.ceil(totalItems / pageSize) || totalItems === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Next
          <ChevronRight size={14} />
        </button>
        {/* Page Jump */}
        {Math.ceil(totalItems / pageSize) > 1 && (
          <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-300">
            <span className="text-sm text-gray-500">Go to:</span>
            <input
              type="number"
              value={jumpToPage}
              onChange={e => onJumpInputChange(e.target.value)}
              onKeyDown={onJumpInputKeyDown}
              placeholder="Page"
              min={1}
              max={Math.ceil(totalItems / pageSize)}
              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={onJumpToPage}
              disabled={!jumpToPage || totalItems === 0}
              className={`px-2 py-1 text-sm rounded transition-colors ${
                !jumpToPage || totalItems === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Go
            </button>
            <span className="text-sm text-gray-500">
              / {Math.ceil(totalItems / pageSize)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
